import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { StorageService } from '../services/storage.service';
import { environment } from '../../environments/environment';

// Here we define list of method which calls the apis

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public baseUrl = environment.apiUrl;
  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private storageService: StorageService,
    private toastr: ToastrService,
  ) {}

  // Function to call regiser api
  userRegister(data?: any): Observable<any> {
    return this.httpClient.post(this.baseUrl + '/register', data);
  }

  // Function to call login api
  loginApi(data?: any): Observable<any> {
    return this.httpClient.post(this.baseUrl + '/users/admin-login', data);
  }

  // Function to call reset-password api
  resetPassword(id?: any, data?: any) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/users/admin/password-update' + id,
      data,
      { observe: 'response', headers },
    );
  }

  // Function to call logout api
  userLogout(id?: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.patch(this.baseUrl + '/users/logout', id, {
      params,
      headers,
    });
  }

  // Function to call get user-profile api
  getSingleUser(id?: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.get(
      this.baseUrl + '/users/get-specific-user/' + id,
      { params, headers },
    );
  }

  // Function to call update business-user api
  updateUser(id?: any, data?: any): Observable<any> {
    const body = { id, data };
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/users/user-update-by-admin',
      body,
      { headers },
    );
  }

  // Function to call changeStatus api(to update active status of an user)
  changeStatus(id?: any, status?: any) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const body = { id, status };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/users/user-status-update-by-admin/',
      body,
      { observe: 'response', headers },
    );
  }

  // Function to call delete user api
  deleteUser(id?: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.delete(
      this.baseUrl + '/users/user-delete-by-admin/' + id,
      { params, headers },
    );
  }

  // Function to call get user list api
  getUsersList(data?: any, subscriber: any = false) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    data.searchdata = data.search.value;
    data.column = data.order[0].column;
    data.dir = data.order[0].dir;
    data.isSubscriber = subscriber;
    return this.httpClient.get(this.baseUrl + '/users/get-all-user', {
      params: data,
      headers,
    });
  }

  getDuplicateAccounts(): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.httpClient.get(this.baseUrl + '/users/admin/duplicate-accounts', {
      headers,
    });
  }

  getEmptyAccounts(): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.httpClient.get(this.baseUrl + '/users/admin/empty-accounts', {
      headers,
    });
  }

  getPlatformInsights(days: number = 30): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams().set('days', days.toString());
    return this.httpClient.get(this.baseUrl + '/users/admin/platform-insights', {
      params,
      headers,
    });
  }

  getReferrers(): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.httpClient.get(this.baseUrl + '/referral/list', { headers });
  }

  getReferrerStats(userId: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.httpClient.get(this.baseUrl + '/referral/stats/' + userId, { headers });
  }

  generateReferrer(payload: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.httpClient.post(this.baseUrl + '/referral/generate', payload, { headers });
  }

  getHobbies() {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.get(this.baseUrl + '/hobbies/get-all', {
      headers,
    });
  }

  addHobby(name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/hobbies/create',
      { name },
      { headers },
    );
  }

  updateHobby(id: number, name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.patch(
      this.baseUrl + '/hobbies/update/' + id,
      { name },
      { headers },
    );
  }

  deleteHobby(id: number) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.delete(this.baseUrl + '/hobbies/delete/' + id, {
      headers,
    });
  }

  getPets() {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.get(this.baseUrl + '/pets/get-all', {
      headers,
    });
  }

  addPets(name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/pets/create',
      { name },
      { headers },
    );
  }
  updatePets(id: number, name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.patch(
      this.baseUrl + '/pets/update/' + id,
      { name },
      { headers },
    );
  }
  deletePets(id: number) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.delete(this.baseUrl + '/pets/delete/' + id, {
      headers,
    });
  }

  getEducations() {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.get(this.baseUrl + '/education/get-all-education', {
      headers,
    });
  }

  addEducations(name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/education/education-create',
      { name },
      { headers },
    );
  }

  updateEducations(id: number, name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.patch(
      this.baseUrl + '/education/update-education/' + id,
      { name },
      { headers },
    );
  }

  deleteEducations(id: number) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.delete(
      this.baseUrl + '/education/delete-education/' + id,
      { headers },
    );
  }

  getReligions() {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.get(this.baseUrl + '/religion/get-all-religion', {
      headers,
    });
  }

  addReligions(name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/religion/religion-create',
      { name },
      { headers },
    );
  }

  updateReligions(id: number, name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.patch(
      this.baseUrl + '/religion/update-religion/' + id,
      { name },
      { headers },
    );
  }

  deleteReligions(id: number) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.delete(
      this.baseUrl + '/religion/delete-religion/' + id,
      { headers },
    );
  }

  getOrientations() {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.get(this.baseUrl + '/orientation/get-all', {
      headers,
    });
  }

  addOrientation(name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/orientation/create',
      { name },
      { headers },
    );
  }
  updateOrientation(id: number, name: string) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.patch(
      this.baseUrl + '/orientation/update/' + id,
      { name },
      { headers },
    );
  }
  deleteOrientation(id: number) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams();
    return this.httpClient.delete(this.baseUrl + '/orientation/delete/' + id, {
      headers,
    });
  }

  // Function to call get revenue api
  getRevenueReport(month: any, year: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    const data: any = { month, year };
    return this.httpClient.get(this.baseUrl + '/subscriber/revenue-data', {
      params: data,
      headers,
    });
  }
  getSubscriptionReport(month: any, year: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    const data: any = { month, year };
    return this.httpClient.get(
      this.baseUrl + '/subscriber/subscription-report-data',
      { params: data, headers },
    );
  }

  // Function to call get dashBoardData api
  dashBoardData(): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.get(this.baseUrl + '/users/get/', {
      headers,
    });
  }

  // Function to call get subscription api
  subscriptionPlansList(data?: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    if (data) {
      data.searchdata = data.search.value;
      data.column = data.order[0].column;
      data.dir = data.order[0].dir;
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.get(this.baseUrl + '/subscription/subscription', {
      params: data,
      headers,
    });
  }

  // Function to call delete subscription api
  deleteSubscriptionPlans(id?: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.delete(
      this.baseUrl + '/subscription/subscription/' + id,
      { headers },
    );
  }

  // Function to call get subscription api
  detailsOfSubscriptionPlans(id?: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.get(
      this.baseUrl + '/subscription/subscription-plan/' + id,
      { headers },
    );
  }

  // Function to call post create-subscription api
  createSubscriptionPlan(data?: any) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const params = new HttpParams();
    return this.httpClient.post(
      this.baseUrl + '/subscription/create-subscription',
      data,
      { headers },
    );
  }

  // Function to call update subscription api
  updateSubscriptionPlan(data?: any, id?: any) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.patch(
      this.baseUrl + '/subscription/subscription-update/' + id,
      data,
      { headers },
    );
  }

  // Function to call post delete-perks/subscription api
  deletePerkOfSubscriptionPlans(id?: any, perkName?: any) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.delete(
      this.baseUrl +
        '/subscription/delete-perks/subscription/' +
        id +
        '/perk/' +
        perkName,
      { observe: 'response', headers },
    );
  }

  // Function to call get subscribers list api
  getSubscriberList(data?: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    data.searchdata = data.search.value;
    data.column = data.order[0].column;
    data.dir = data.order[0].dir;
    return this.httpClient.get(this.baseUrl + '/subscribers', {
      params: data,
      headers,
    });
  }

  // Function to call get subscribe-user api by userId
  getDetailsOfSubscribedUser(id?: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.get(this.baseUrl + '/subscribe-user/' + id, {
      headers,
    });
  }

  isRealTimeImageVerified(id?: any, data?: any) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const body = data;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.patch(
      this.baseUrl + `/users/isRealTimeImageVerified-byAdmin/${id}`,
      body,
      { observe: 'response', headers },
    );
  }

  isPrescriptionVerified(id?: any, data?: any) {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const body = data;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams();
    return this.httpClient.patch(
      this.baseUrl + `/users/isPrescriptionVerified-byAdmin/${id}`,
      body,
      { observe: 'response', headers },
    );
  }

  getBroadcastHistory(page: number = 1, limit: number = 20): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));
    return this.httpClient.get(this.baseUrl + '/users/admin/broadcast-history', {
      headers,
      params,
    });
  }

  getConsultantsForVerification(
    verificationStatus: string = 'pending',
    page: number = 1,
    limit: number = 20,
  ): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const params = new HttpParams()
      .set('verificationStatus', verificationStatus)
      .set('page', String(page))
      .set('limit', String(limit));

    return this.httpClient.get(this.baseUrl + '/consultant/admin/all', {
      params,
      headers,
    });
  }

  verifyConsultant(
    consultantId: string,
    action: 'approve' | 'reject',
    rejectionReason?: string,
  ): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const body: any = { action };

    if (action === 'reject') {
      body.rejectionReason =
        rejectionReason && rejectionReason.trim()
          ? rejectionReason.trim()
          : 'No reason provided.';
    }

    return this.httpClient.patch(
      this.baseUrl + '/consultant/admin/verify/' + consultantId,
      body,
      { headers },
    );
  }

  broadcastNotification(payload: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData?.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.httpClient.post(this.baseUrl + '/users/admin/broadcast', payload, { headers });
  }

  markAffiliateCommissionsAsPaid(affiliateId: any): Observable<any> {
    const sessionData = this.storageService.loadSessionData();
    const token = sessionData.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.httpClient.post(
      this.baseUrl + '/referral/mark-as-paid',
      { affiliateId },
      { headers }
    );
  }
}
